import { useMemo, useState } from 'react';
import { useAccountsCrumb } from './lib/usePhoneCrumb';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button, Card, Chip, DChip, DataTable, Icon, Modal, Pane, PaneHead, TabBar } from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useNarrowViewport, useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import { PeriodRow } from './components/PeriodRow';
import { exportRows } from './lib/exportCsv';
import { exportStatementsPdf } from './lib/exportPdf';
import apiClient from '@/services/apiClient';
import { useTenantStore } from '@/stores/useTenantStore';
import {
  DEAL_LABEL,
  PAY_METHOD_LABEL,
  amount,
  hasPiastres,
  money,
  monthName,
  thisPeriod,
  usePayrollActions,
  periodLabel,
  usePayrollRun,
  type PayrollRun,
  useAccountsSummary,
  type RunLine,
  type Statement,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';
import { AdjustModal } from './components/AdjustModal';
import { PhoneTabs } from './components/PhoneTabs';

/**
 * People we pay — `g_people` (6), `g_m_sendmgr` (6b), `g_m_adj` (7c),
 * `g_settle` (8). TWO LANES, ONE RECKONING.
 *
 * SEPARATION OF DUTIES IS THE SUBJECT, not a caption. The accountant works
 * out what everyone is owed and sends it; the manager releases it. This screen
 * offers Send and never Approve, because the person holding it cannot do the
 * second — and the backend refuses it too, so the button is absent rather than
 * disabled-and-lying.
 *
 * SENDING FREEZES THE FIGURES. While the run is a draft it recomputes as money
 * arrives; the moment it goes for approval every number becomes a row. A
 * manager approves an amount, not a formula that will read differently
 * tomorrow.
 *
 * ROOM RENT RUNS THE OTHER WAY. A doctor who rents a room owes the clinic, so
 * their line is something to COLLECT and is kept out of "net to pay" while
 * staying in the same run — it is the same monthly reckoning with the same
 * person, and moving it elsewhere is how it gets forgotten.
 */

/** "· 2 days absent −580 · advance −500" — what moved a salaried net, in the row. */
function slipNotes(l: RunLine): string {
  const s = l.slip;
  if (!s) return '';
  const parts: string[] = [];
  if (s.overtimeMinor) parts.push(`overtime +${amount(s.overtimeMinor)}`);
  if (s.absenceDays) parts.push(`${s.absenceDays} day${s.absenceDays === 1 ? '' : 's'} absent −${amount(s.absenceMinor)}`);
  if (s.advanceMinor) parts.push(`advance −${amount(s.advanceMinor)}`);
  if (s.socialInsuranceMinor) parts.push(`social insurance −${amount(s.socialInsuranceMinor)}`);
  return parts.length ? ` · ${parts.join(' · ')}` : '';
}

export function PeoplePage() {
  const go = useNavigate();
  const [period, setPeriod] = useState(thisPeriod);
  const [pickedId, setPicked] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [params, setParams] = useSearchParams();
  const narrow = useNarrowViewport();
  /* `staff` is the phone's staff-only payroll screen (g_e5): the payslip lane
     on its own, with its own two tiles. The desk keeps one run with two lanes. */
  const tab: 'run' | 'settlement' | 'staff' = params.get('tab') === 'settlement' ? 'settlement'
    : params.get('tab') === 'staff' && narrow ? 'staff' : 'run';
  const setTab = (next: 'run' | 'settlement' | 'staff') => {
    const p = new URLSearchParams(params);
    if (next === 'run') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
    setPicked(null);
  };
  const [sending, setSending] = useState(false);
  const [adjusting, setAdjusting] = useState<RunLine | null>(null);
  const [paying, setPaying] = useState<RunLine | null>(null);

  const roleCheck = useAuthStore((s) => s.roleCheck);
  const canWrite = roleCheck.hasPermission(Permission.PAYOUT_WRITE);
  const canApprove = roleCheck.hasPermission(Permission.PAYOUT_APPROVE);
  const canOpenManager = roleCheck.hasPermission(Permission.HOSPITAL_SETTINGS_READ);

  const { data: run, isLoading, isError } = usePayrollRun(period);
  const { data: summary } = useAccountsSummary(period);
  const act = usePayrollActions();
  const past1 = usePayrollRun(shiftPeriod(period, -1));
  const past2 = usePayrollRun(shiftPeriod(period, -2));

  usePageMeta({ title: 'People we pay' });

  const lanes = useMemo(() => {
    const q = query.trim().toLowerCase();
    const lines = (run?.lines ?? []).filter(
      (l) => !q || `${l.personName} ${l.dealSummary ?? ''}`.toLowerCase().includes(q),
    );
    return {
      doctors: lines.filter((l) => l.lane === 'doctors'),
      staff: lines.filter((l) => l.lane === 'staff'),
    };
  }, [run, query]);

  const picked = (run?.lines ?? []).find((l) => key(l) === pickedId) ?? null;
  const status = run?.status ?? 'draft';
  /* The four totals are meant to add up; print them to the piastre when any has one. */
  const precise = !!run && hasPiastres(run.grossMinor, run.deductionsMinor, run.additionsMinor, run.netMinor);
  const pm = (v: number) => money(v, 'EGP', precise);

  /* The verb this state wants, and only the one this person may do. */
  const primary = (() => {
    if (!run?.lines.length) return undefined;
    if (status === 'draft' && canWrite) {
      return { label: 'Send to manager ▸', onClick: () => setSending(true) };
    }
    if (status === 'sent' && canApprove) {
      return {
        label: '✓ Approve the run',
        onClick: () =>
          act.approve.mutate(period, {
            onSuccess: () => toast.success(`${monthName(period)} approved — it can be paid now`),
            onError: () => toast.error('It was not approved.'),
          }),
      };
    }
    return undefined;
  })();

  const crumb = useAccountsCrumb('People we pay', monthName(period));
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      phoneMenu: (close) => <AccountantMenu door="people" onClose={close} />,
      title: tab === 'settlement' ? `Payout run — ${monthName(period)}` : 'People we pay',
      sub:
        tab === 'settlement' ? (
          <>
            <a
              href="#run"
              className="brand t-strong"
              onClick={(e) => {
                e.preventDefault();
                setTab('run');
              }}
            >
              ‹ People we pay
            </a>{' '}
            ▸ settlement <StatusChip status={status} />
          </>
        ) : (
          <>
            {monthName(period)} · two lanes · cut-off {run?.cutOff ?? '—'}{' '}
            <StatusChip status={status} />
          </>
        ),
      search: tab === 'settlement' ? 'Search a person, a reference…' : 'Search a person, a deal…',
      searchValue: query,
      onSearchChange: setQuery,
      ...(primary ? { primary } : {}),
      secondary: {
        label: 'Export',
        onClick: () =>
          exportRows(
            `payroll-${period}`,
            ['Lane', 'Person', 'Deal', 'Base', 'Gross', 'Deductions', 'Additions', 'Net', 'Status'],
            (run?.lines ?? []).map((l) => [
              l.lane,
              l.personName,
              l.dealSummary ?? l.dealKind,
              (l.baseMinor / 100).toFixed(2),
              (l.grossMinor / 100).toFixed(2),
              (l.deductionsMinor / 100).toFixed(2),
              (l.additionsMinor / 100).toFixed(2),
              (l.netMinor / 100).toFixed(2),
              l.status,
            ]),
          ),
      },
      pane: tab === 'settlement' ? (
        <HistoryPane past={[[shiftPeriod(period, -1), past1.data], [shiftPeriod(period, -2), past2.data]]} onOpen={setPeriod} />
      ) : picked ? (
        <PersonPane
          line={picked}
          runStatus={status}
          canWrite={canWrite}
          onAdjust={() => setAdjusting(picked)}
          onStatement={() => go(`/accountant/people/${picked.personId}?period=${period}`)}
          onSettle={() => setPaying(picked)}
          onClose={() => setPicked(null)}
        />
      ) : (
        <Pane
          head={
            <PaneHead
              overline="Selected person"
              title="Nothing picked"
              sub="pick somebody to see their deal"
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              Six deal types: % of collected · fixed salary · per visit · hybrid · room rent
              (reverse) · commission.
            </p>
          </div>
        </Pane>
      ),
    },
    [picked, status, canWrite, canApprove, period, run?.lines.length, run?.cutOff, pickedId, tab, query, past1.data, past2.data],
  );

  const lineCells = (l: RunLine) => ({
    person: (
      <div style={{ minWidth: 0 }}>
        <p className="t-strong">
          {l.personName}
          {l.lane === 'staff' && l.roleName ? <span className="mut"> — {l.roleName}</span> : null}
          {l.dealKind === 'room_rent' ? (
            <>
              {' '}
              <Chip kind="outline" small>reverse</Chip>
            </>
          ) : null}
        </p>
        <p className="t-cap mut">
          {l.dealSummary ?? DEAL_LABEL[l.dealKind]}
          {slipNotes(l)}
          {l.note ? ` · ${l.note}` : ''}
        </p>
      </div>
    ),
    gross: (
      <span className={`t-strong num${l.reverse ? ' mut' : ''}`}>
        {l.grossMinor ? amount(Math.abs(l.grossMinor)) : '—'}
      </span>
    ),
    deduct: <span className="mut num">{l.deductionsMinor ? `−${amount(l.deductionsMinor)}` : '—'}</span>,
    net: narrow ? (
      <span className={`t-strong num${l.reverse ? ' amb' : ''}`}>
        {l.reverse ? '−' : ''}
        {amount(Math.abs(l.netMinor))} <LineChip line={l} />
      </span>
    ) : (
      <span className={`t-strong num${l.reverse ? ' amb' : ''}`}>
        {l.reverse ? '−' : ''}
        {amount(Math.abs(l.netMinor))}
      </span>
    ),
    status: <LineChip line={l} />,
  });

  const sum = (lines: RunLine[], f: (l: RunLine) => number) => lines.reduce((n, l) => n + f(l), 0);

  const laneRows = (heading: string, who: string, lines: RunLine[]): TableRow[] => {
    if (!lines.length) return [];
    const rows: TableRow[] = [
      { id: `sec:${who}`, cells: {}, section: `${heading} · ${lines.length} ${lines.length === 1 ? 'person' : 'people'}` },
      ...lines.map((l) => ({ id: key(l), cells: lineCells(l) })),
    ];
    /* A room rent runs the other way — it is money to COLLECT, not part of what is
       paid, so the subtotal adds up the same lines the run's headline does. */
    const owing = lines.filter((l) => !l.reverse);
    if (!narrow) {
      rows.push({
        id: `sub:${who}`,
        cells: {
          person: <span className="t-strong">{who} — subtotal, {owing.length === lines.length ? `all ${lines.length}` : `${owing.length} paid · ${lines.length - owing.length} to collect`}</span>,
          gross: <span className="t-strong num">{amount(sum(owing, (l) => l.grossMinor), precise)}</span>,
          deduct: <span className="t-strong num amb">{sum(owing, (l) => l.deductionsMinor) ? `−${amount(sum(owing, (l) => l.deductionsMinor), precise)}` : '—'}</span>,
          net: <span className="t-strong num">{amount(sum(owing, (l) => l.netMinor), precise)}</span>,
          status: null,
        },
      });
    }
    return rows;
  };

  const columns = narrow
    ? [
        { key: 'person', label: 'Person & deal', width: 60 },
        { key: 'net', label: 'Net', width: 40, role: 'trailing' as const },
      ]
    : [
        { key: 'person', label: 'Person & deal', width: 46 },
        { key: 'gross', label: 'Gross', width: 13 },
        { key: 'deduct', label: 'Deduct.', width: 13 },
        { key: 'net', label: 'Net', width: 13 },
        { key: 'status', label: 'Status', width: 15 },
      ];

  const allLines = [...lanes.doctors, ...lanes.staff];
  /* THE STAFF LANE GROUPS BY ROLE — receptionists, nurses, technicians —
     because a clinic reads its payroll by who does the work, not by one list. */
  const staffByRole = useMemo(() => {
    const byRole = new Map<string, RunLine[]>();
    for (const l of lanes.staff) {
      const role = l.roleName ? l.roleName.replace(/^./, (c) => c.toUpperCase()) : 'Staff';
      byRole.set(role, [...(byRole.get(role) ?? []), l]);
    }
    return [...byRole.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [lanes.staff]);
  const settleRows: TableRow[] = allLines.map((l) => ({
    id: key(l),
    cells: {
      person: <span className="t-strong">{l.personName}</span>,
      net: <span className={`t-strong num${l.reverse ? ' amb' : ''}`}>{l.reverse ? '−' : ''}{amount(Math.abs(l.netMinor))}</span>,
      by: l.reverse ? (
        <span className="mut">to collect</span>
      ) : l.paidMethod ? (
        <span>
          <span className="t-sec">{PAY_METHOD_LABEL[l.paidMethod] ?? l.paidMethod}</span>
          {l.paidReference ? <span className="t-cap mut" style={{ display: 'block' }}>#{l.paidReference}</span> : null}
        </span>
      ) : (
        <span className="mut">{l.note || '—'}</span>
      ),
      state:
        l.status === 'settled' ? (
          <Chip kind="success" small>paid ✓ {l.settledAt ? when(l.settledAt).split(',')[0] : ''}</Chip>
        ) : l.status === 'approved' ? (
          <Chip kind="warn" small>approved · unpaid</Chip>
        ) : l.status === 'to_collect' ? (
          <Chip kind="warn" small>invoice them</Chip>
        ) : (
          <Chip kind="outline" small>awaiting approval</Chip>
        ),
    },
  }));

  const clinic = useTenantStore((st) => st.publicConfig)?.displayName ?? 'Clinic';
  const [building, setBuilding] = useState(false);
  const buildStatements = async () => {
    setBuilding(true);
    try {
      const got = await Promise.all(
        allLines.map(async (l) => {
          const res = await apiClient.get('/payroll/statement', { params: { personId: l.personId, period } });
          const b = res.data as { data?: Statement };
          return (b?.data ?? (b as unknown as Statement)) as Statement;
        }),
      );
      exportStatementsPdf(
        `statements-${period}`,
        clinic,
        `Statements — ${monthName(period)} ${period.slice(0, 4)}`,
        got.map((d) => ({
          name: d.personName,
          deal: d.dealSummary ?? DEAL_LABEL[d.dealKind],
          columns: ['Date', 'Source', 'Collected', 'Share', 'Earned'],
          rows: d.lines.map((x) => [
            x.date,
            x.source,
            amount(x.collectedMinor),
            x.sharePercent === null ? '' : `${x.sharePercent}%`,
            amount(x.earnedMinor),
          ]),
          totals: [
            ['Gross', amount(d.grossMinor)],
            ['Deductions', amount(d.deductionsMinor)],
            ['Additions', amount(d.additionsMinor)],
            ['Net', amount(d.netMinor)],
          ],
        })),
      );
    } catch {
      toast.error('The statements were not built.');
    } finally {
      setBuilding(false);
    }
  };

  const onPick = (r: TableRow) => {
    if (r.id.startsWith('sub:') || r.id.startsWith('sec:')) return;
    setPicked(r.id === pickedId ? null : r.id);
  };

  return (
    <div className="maie desk acc-root">
      <PeriodRow period={period} onChange={setPeriod} branches={summary?.branches} />

      <div className="dcontent scroll">
        <PhoneTabs active="people" />
{narrow ? (
          <p className="flex mb12" style={{ gap: 8 }}>
            <Chip kind={tab === 'run' ? 'brand' : 'outline'} small onClick={() => setTab('run')}>The run</Chip>
            <Chip kind={tab === 'staff' ? 'brand' : 'outline'} small onClick={() => setTab('staff')}>Staff</Chip>
            <Chip kind={tab === 'settlement' ? 'brand' : 'outline'} small onClick={() => setTab('settlement')}>Settlement</Chip>
          </p>
        ) : (
                <TabBar
          label="People we pay"
          items={[
            { key: 'run' as const, label: 'The run' },
            { key: 'settlement' as const, label: 'Settlement' },
          ]}
          active={tab}
          onSelect={setTab}
        />
        )}

        {tab === 'staff' ? (
          <>
            <div className="acc-stats" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <div className="card" style={{ margin: 0, background: 'var(--info-100)' }}>
                <p className="t-ovl mut">Gross</p>
                <p className="t-dxl mt8">{money(sum(lanes.staff, (l) => l.grossMinor))}</p>
              </div>
              <div className="card" style={{ margin: 0, background: 'var(--info-100)' }}>
                <p className="t-ovl mut">Net to pay</p>
                <p className="t-dxl mt8">{money(sum(lanes.staff, (l) => l.netMinor))}</p>
              </div>
            </div>
            {lanes.staff.length ? (
              <DataTable
                caption="Staff payroll"
                columns={columns}
                rows={lanes.staff.map((l) => ({ id: key(l), cells: lineCells(l) }))}
                selectedId={pickedId}
                onSelect={onPick}
                empty={<div />}
              />
            ) : (
              <div className="emptystate">
                <p className="es-t">No staff on {monthName(period)}'s run</p>
                <p className="t-sec mut">Salaried people appear here once they have a fixed salary set.</p>
              </div>
            )}
            {lanes.staff.length ? (
              <>
                <Card className="mt12">
                  <p className="t-sec sec">
                    Absences come from the ATTENDANCE the roster already records — not retyped.
                  </p>
                </Card>
                <Card kind="warn" className="mt8">
                  <p className="t-sec amb">
                    Advances are tracked as a running balance and deducted until cleared.
                  </p>
                </Card>
              </>
            ) : null}
            {status === 'draft' && canWrite && lanes.staff.length ? (
              <Button kind="primary" style={{ marginTop: 16, width: '100%' }} onClick={() => setSending(true)}>
                Send to the clinic manager to approve
              </Button>
            ) : null}
          </>
        ) : tab === 'run' ? (
          <>
            <div className="acc-stats" style={narrow ? { gridTemplateColumns: 'repeat(2, 1fr)' } : undefined}>
              {narrow ? (
                <>
                  <div className="card" style={{ margin: 0 }}>
                    <p className="t-ovl brand">Owed now</p>
                    <p className="t-dxl mt8">{pm(run?.netMinor ?? 0)}</p>
                  </div>
                  <div className="card" style={{ margin: 0 }}>
                    <p className="t-ovl brand">To collect</p>
                    <p className={`t-dxl mt8${run?.toCollectMinor ? ' grn' : ''}`}>{money(run?.toCollectMinor ?? 0)}</p>
                  </div>
                </>
              ) : null}
              {narrow ? null : (
                <div className="card" style={{ margin: 0 }}>
                  <p className="t-ovl mut">Base — the % deals</p>
                  <p className="t-dxl mt8">{money(run?.baseMinor ?? 0)}</p>
                  <p className="t-cap mut mt4">what the shares were taken of</p>
                </div>
              )}
              {narrow ? null : (
                <>
              <div className="card" style={{ margin: 0 }}>
                <p className="t-ovl mut">Gross payable</p>
                <p className="t-dxl mt8">{pm(run?.grossMinor ?? 0)}</p>
              </div>
              <div className="card" style={{ margin: 0 }}>
                <p className="t-ovl mut">Deductions</p>
                <p className={`t-dxl mt8${run?.deductionsMinor ? ' amb' : ''}`}>
                  {run?.deductionsMinor ? '−' : ''}{pm(run?.deductionsMinor ?? 0)}
                </p>
                {run?.additionsMinor ? (
                  <p className="t-cap grn mt4">and +{amount(run.additionsMinor, precise)} overtime added</p>
                ) : null}
              </div>
              <div className="card" style={{ margin: 0 }}>
                <p className="t-ovl mut">Net to pay</p>
                <p className="t-dxl mt8">{pm(run?.netMinor ?? 0)}</p>
                {run?.toCollectMinor ? (
                  <p className="t-cap amb mt4">and {amount(run.toCollectMinor)} to collect</p>
                ) : null}
              </div>
                </>
              )}
            </div>

            {allLines.length ? (
              <DataTable
                caption="People we pay"
                columns={columns}
                rows={[
                  ...laneRows('Doctors — their deals', 'Doctors', lanes.doctors),
                  ...staffByRole.flatMap(([role, people]) =>
                    laneRows(`Staff · ${role}`, `Staff · ${role}`, people),
                  ),
                ]}
                selectedId={pickedId}
                onSelect={onPick}
                empty={<div />}
              />
            ) : null}

            {allLines.length ? (
              <p className="mt12" style={{ textAlign: 'right' }}>
                <Button kind="outline" disabled={building} onClick={() => void buildStatements()}>
                  ⬇ Statements — one PDF per person
                </Button>
              </p>
            ) : null}

            {!run?.lines.length ? (
              <div className="emptystate">
                {isLoading ? (
                  <p className="t-sec mut">Working out the month…</p>
                ) : isError ? (
                  <p className="t-sec mut">The run did not come back. Nothing has been changed.</p>
                ) : (
                  <>
                    <p className="es-t">Nobody to pay in {monthName(period)}</p>
                    <p className="t-sec mut">
                      A run is built from the deals the clinic has set and the money it collected.
                      Nobody has a deal, and nothing was collected against anybody.
                    </p>
                  </>
                )}
              </div>
            ) : null}

            {status === 'draft' ? (
              <p className="t-sec mut mt16">
                While it is a draft this recomputes as money arrives. Sending it for approval freezes
                every figure — the manager approves an amount, not a formula.
              </p>
            ) : null}
            {narrow && status === 'draft' && canWrite && run?.lines.length ? (
              <Button kind="primary" style={{ marginTop: 16 }} onClick={() => setSending(true)}>
                Send to the clinic manager to approve
              </Button>
            ) : null}
          </>
        ) : (
          <>
            {run ? <RunProgress run={run} narrow={narrow} /> : null}
            {!narrow && settleRows.length ? (
              <DataTable
                caption="Settlement"
                columns={[
                  { key: 'person', label: 'Person', width: 34 },
                  { key: 'net', label: 'Net', width: 16 },
                  { key: 'by', label: 'Paid by', width: 26 },
                  { key: 'state', label: 'State', width: 24 },
                ]}
                rows={settleRows}
                empty={<div />}
              />
            ) : null}
            {!narrow ? (
              <>
            <p className="t-sec mut mt16">
              Approving locks the period — later corrections are adjustments in the NEXT run, never
              edits to a settled one.
            </p>
            <Card kind="accent" className="mt12">
              <p className="t-sec brand">
                Approving, denying and recording how each person was paid are the clinic manager’s
                controls — the run is locked to you while it is with them. If it is returned, it
                comes back with their note.
              </p>
            </Card>
                {canOpenManager ? (
                  <Button kind="outline" style={{ marginTop: 12 }} onClick={() => go('/clinic-manager/alerts')}>
                    Open the clinic manager’s settlement
                  </Button>
                ) : null}
              </>
            ) : null}
            {narrow ? (
              <>
                <p className="t-ovl mut mt16">After approval</p>
                <Card>
                  <p className="t-sec">
                    Each row marked paid with method and date · payslips and statements sent · the
                    whole run posts to Expenses
                  </p>
                </Card>
                {(run?.lines ?? [])
                  .filter((l) => l.reverse && l.netMinor !== 0)
                  .map((l, i) => (
                    <Card key={`${l.personId}-${i}`}>
                      <p className="t-sec">
                        {l.personName}’s {amount(Math.abs(l.netMinor))} room rent moves to{' '}
                        <b>to collect</b>, not to pay
                      </p>
                    </Card>
                  ))}
              </>
            ) : null}
          </>
        )}
      </div>

      <PayModal
        line={paying}
        onClose={() => setPaying(null)}
        onPay={(method, reference) => {
          const line = paying;
          if (!line?.id) return;
          act.settle.mutate(
            { lineId: line.id, method, reference },
            {
              onSuccess: () => toast.success(`${line.personName} paid`),
              onError: (e: unknown) => toast.error(errText(e, 'It was not settled.')),
            },
          );
          setPaying(null);
        }}
      />

      <SendModal
        open={sending}
        run={run ?? null}
        onClose={() => setSending(false)}
        onSend={() => {
          act.send.mutate(period, {
            onSuccess: () => toast.success('Sent to the manager — the figures are frozen'),
            onError: (e: unknown) => toast.error(errText(e, 'It was not sent.')),
          });
          setSending(false);
        }}
      />

      <AdjustModal
        open={!!adjusting}
        who={adjusting?.personName ?? ''}
        period={period}
        onClose={() => setAdjusting(null)}
        onSave={(kind, amountMinor, reason) => {
          if (!adjusting) return;
          act.addAdjustment.mutate(
            { doctorId: adjusting.personId, period, kind, amountMinor, reason },
            {
              onSuccess: () =>
                toast.success(
                  `${kind === 'deduction' ? 'Deduction' : 'Addition'} added to ${adjusting.personName}`,
                ),
              onError: (e: unknown) => toast.error(errText(e, 'It was not added.')),
            },
          );
          setAdjusting(null);
        }}
      />
    </div>
  );
}

/** `2026-08` shifted by whole months. */
function shiftPeriod(period: string, by: number): string {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** The run's own lineage — `g_settle`'s right pane. */
/** How was she paid — the method, and the reference it can be found by. */
function PayModal({
  line,
  onClose,
  onPay,
}: {
  line: RunLine | null;
  onClose: () => void;
  onPay: (method: string, reference?: string) => void;
}) {
  const [method, setMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  return (
    <Modal
      open={!!line}
      onClose={onClose}
      title={`Mark ${line?.personName ?? ''} paid`}
      sub={line ? `${money(Math.abs(line.netMinor))} — record how, so it can be found later` : ''}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Not yet</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              onPay(method, reference.trim() || undefined);
              setReference('');
            }}
          >
            Mark paid
          </button>
        </>
      }
    >
      <p className="t-ovl mut mt12">Paid by</p>
      <div className="flex mt8" style={{ gap: 8, flexWrap: 'wrap' }}>
        {Object.entries(PAY_METHOD_LABEL).map(([k, label]) => (
          <button key={k} type="button" aria-pressed={method === k} className={`chip ${method === k ? 'chip-brand' : 'chip-outline'}`} style={{ cursor: 'pointer' }} onClick={() => setMethod(k)}>
            {label}
          </button>
        ))}
      </div>
      <div className="mt12">
        <label className="t-ovl mut" htmlFor="pay-ref">Reference</label>
        <input id="pay-ref" className="fld mt4" placeholder="Transfer or cheque number" value={reference} onChange={(e) => setReference(e.target.value)} />
      </div>
    </Modal>
  );
}

function HistoryPane({
  past,
  onOpen,
}: {
  past: Array<[string, PayrollRun | null | undefined]>;
  onOpen: (p: string) => void;
}) {
  const real = past.filter(([, r]) => r && r.lines.length);
  return (
    <Pane
      head={<PaneHead overline="This run" title="Run history" sub="its own lineage" />}
      foot={
        <Button kind="outline" style={{ width: '100%' }} disabled={!real.length} onClick={() => real[0] && onOpen(real[0][0])}>
          Open a past run
        </Button>
      }
    >
      {real.length ? (
        real.map(([p, r]) => (
          <Card key={p}>
            <div className="flex" style={{ justifyContent: 'space-between' }}>
              <p className="t-strong">{monthName(p)} {p.slice(0, 4)}</p>
              {r?.locked ? (
                <Chip kind="success" small>locked</Chip>
              ) : (
                <StatusChip status={r?.status ?? 'draft'} />
              )}
            </div>
            <p className="t-cap mut mt4">
              {money(r?.netMinor ?? 0)} net · {r?.lines.length} people
            </p>
          </Card>
        ))
      ) : (
        <Card>
          <p className="t-sec sec">No earlier run on file for {periodLabel(past[0]?.[0] ?? '')} or the month before.</p>
        </Card>
      )}
      <Card kind="accent">
        <p className="t-sec brand">
          <b>Disputes:</b> a doctor flags a statement line; it returns to the accountant with the
          note, and resolves as an adjustment in the next run.
        </p>
      </Card>
    </Pane>
  );
}

const key = (l: RunLine) => l.id ?? `p:${l.personId}`;

const errText = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? fallback;

function StatusChip({ status }: { status: string }) {
  if (status === 'draft') return <Chip kind="warn" small>draft</Chip>;
  if (status === 'sent') return <Chip kind="info" small>awaiting approval</Chip>;
  if (status === 'approved') return <Chip kind="success" small>approved</Chip>;
  return <Chip kind="success" small>settled ✓</Chip>;
}

function LineChip({ line }: { line: RunLine }) {
  if (line.status === 'to_collect') return <Chip kind="warn" small>to collect</Chip>;
  if (line.status === 'settled') return <Chip kind="success" small>settled ✓</Chip>;
  if (line.status === 'approved') return <Chip kind="warn" small>approved</Chip>;
  return <Chip kind="outline" small>draft</Chip>;
}

/** The deal, read-only — it is set by the manager in Settings ▸ Money. */
function PersonPane({
  line,
  runStatus,
  canWrite,
  onAdjust,
  onSettle,
  onStatement,
  onClose,
}: {
  line: RunLine;
  runStatus: string;
  canWrite: boolean;
  onAdjust: () => void;
  onSettle: () => void;
  onStatement: () => void;
  onClose: () => void;
}) {
  const payable = runStatus === 'approved' && line.status === 'approved' && !line.reverse;
  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="Selected person"
          title={`Deal — ${line.personName}`}
          sub={line.dealSummary ?? DEAL_LABEL[line.dealKind]}
          wrapTitle
        />
      }
      foot={
        <>
          <button
            type="button"
            className="btn btn-outline"
            style={{ flex: 1, minHeight: 44 }}
            onClick={onStatement}
          >
            Open statement
          </button>
          {canWrite && payable ? (
            <button
              type="button"
              className="btn btn-primary"
              style={{ flex: 1, minHeight: 44 }}
              onClick={onSettle}
            >
              Mark paid
            </button>
          ) : null}
        </>
      }
    >
      <Card>
        <p className="t-ovl mut">Type</p>
        <p className="t-sec sec mt4">{DEAL_LABEL[line.dealKind]}</p>
        <p className="t-cap mut mt8">
          set by the manager in Settings ▸ Money — read-only here
        </p>
      </Card>

      <Card>
        <p className="t-ovl mut">This month</p>
        <p className="t-sec sec mt4">
          {line.reverse
            ? `${amount(Math.abs(line.netMinor))} to collect`
            : `${amount(line.grossMinor)} gross`}
          {line.deductionsMinor ? ` − ${amount(line.deductionsMinor)} deductions` : ''}
          {line.additionsMinor ? ` + ${amount(line.additionsMinor)} additions` : ''}
        </p>
        <p className="t-strong mt8">
          {line.reverse ? 'Owed to the clinic' : 'Net'} {amount(Math.abs(line.netMinor))}
        </p>
      </Card>

      {line.reverse ? (
        <Card kind="accent">
          <p className="t-sec brand">
            The money runs the other way here: they rent the room, so this is something to collect
            rather than to pay. It stays in the run because it is the same monthly reckoning.
          </p>
          <p className="t-cap mut mt8">
            Payout adjustments are not applied to it. A “deduction” means something clear on a
            payout and nothing clear on a bill, so the rent is changed, or a separate charge
            raised — both of which say what they mean.
          </p>
        </Card>
      ) : null}

      <Card>
        <p className="t-ovl mut">Period</p>
        <p className="t-sec sec mt4">Monthly · cut-off last day</p>
        <p className="t-cap mut mt8">
          money collected after cut-off rolls to the next period
        </p>
      </Card>

      {canWrite && runStatus === 'draft' && !line.reverse ? (
        <p className="mt8">
          <DChip kind="warn" onClick={onAdjust}>+ Add an adjustment</DChip>
        </p>
      ) : null}

      <Card kind="accent">
        <p className="t-sec brand">
          Six deal types: % of collected · fixed salary · per visit · hybrid · room rent (reverse) ·
          commission
        </p>
      </Card>
    </Pane>
  );
}

/** Send the run for approval — `g_m_sendmgr`, separation of duties. */
function SendModal({
  open,
  run,
  onClose,
  onSend,
}: {
  open: boolean;
  run: { period: string; netMinor: number; toCollectMinor: number; lines: RunLine[] } | null;
  onClose: () => void;
  onSend: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Send the ${run ? monthName(run.period).split(' ')[0] : ''} run for approval`}
      sub="the separation-of-duties moment — it leaves your hands"
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={onSend}>
            Send to the clinic manager
          </button>
        </>
      }
    >
      <Card className="mt12">
        <p className="t-ovl mut">The {run ? monthName(run.period).split(' ')[0].toUpperCase() : ''} run</p>
        <p className="t-sec sec mt4">
          {run?.lines.length} people · {money(run?.netMinor ?? 0)} to pay
          {run?.toCollectMinor ? ` · ${amount(run.toCollectMinor)} to collect` : ''}
        </p>
      </Card>
      <Card className="mt12">
        <p className="t-ovl mut">What happens next</p>
        <p className="t-sec sec mt4">
          The clinic manager approves per person or the whole run, then records how each was paid
          (method + reference).
        </p>
      </Card>
      <Card className="mt12" kind="warn">
        <p className="t-sec amb">
          You cannot approve your own run, and the manager cannot edit its figures. While it is with
          them the run is locked to you.
        </p>
      </Card>
    </Modal>
  );
}

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
      })
    : '';

/**
 * WHERE A SENT RUN IS (`g_e7`). Once it leaves the accountant the figures are
 * frozen, so the question stops being "what is owed" and becomes "who has it
 * now": prepared, waiting on the manager, approved, settled.
 */
function RunProgress({ run, narrow }: { run: PayrollRun; narrow: boolean }) {
  const owing = run.lines.filter((l) => !l.reverse);
  const lane = (k: 'doctors' | 'staff') =>
    owing.filter((l) => l.lane === k).reduce((n, l) => n + l.netMinor, 0);
  /* The two lanes are printed to the piastre when either has one — rounding
     each to the pound made 29 + 48,560 sit under a headline of 48,588. */
  const exact = hasPiastres(lane('doctors'), lane('staff'), run.netMinor);
  return (
    <div className="mb16">
      {narrow ? (
      <div className="card" style={{ borderColor: 'var(--brand-300)' }}>
        <p className="t-ovl mut">{monthName(run.period)} · everyone</p>
        <p className="t-dxl mt4">{money(run.netMinor, 'EGP', exact)}</p>
        <p className="t-cap mut">
          doctors {amount(lane('doctors'), exact)} · staff {amount(lane('staff'), exact)}
        </p>
      </div>
      ) : null}
      {run.status === 'draft' ? (
        <div className="card flex" style={{ gap: 12, background: 'var(--warning-100)', borderColor: 'var(--warning-600)' }}>
          <Icon name="clock" className="amb" />
          <div>
            <p className="t-strong">Not sent yet — still a draft</p>
            <p className="t-cap mut">the figures recompute as money arrives, until you send it to the manager</p>
          </div>
        </div>
      ) : (
        <div style={narrow ? undefined : { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
      <div className="card flex" style={{ gap: 12 }}>
        <Icon name="check" className="grn" />
        <div>
          <p className="t-strong">Prepared by {run.sentBy ?? 'the accountant'} — accountant</p>
          <p className="t-cap mut">{when(run.sentAt)}</p>
        </div>
      </div>
      {run.status === 'sent' ? (
        <>
          <div className="card flex" style={{ gap: 12, background: 'var(--warning-100)', borderColor: 'var(--warning-600)' }}>
            <Icon name="clock" className="amb" />
            <div>
              <p className="t-strong">Waiting for the clinic manager</p>
              <p className="t-cap mut">approves, then pays</p>
            </div>
          </div>
          {narrow ? (
          <div className="card">
            <p className="t-sec">
              The accountant cannot approve their own run · the manager cannot edit its figures
            </p>
          </div>
          ) : null}
        </>
      ) : (
        <div className="card flex" style={{ gap: 12 }}>
          <Icon name="check" className="grn" />
          <div>
            <p className="t-strong">Approved by {run.approvedBy ?? 'the manager'} — clinic manager</p>
            <p className="t-cap mut">
              {when(run.approvedAt)}
              {run.status === 'settled' ? ' · everyone owed has been paid' : ' · each person is marked paid as the money goes'}
            </p>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
}

